import { useLocation, Link } from '@tanstack/react-router';

interface SidebarProps {
  isOpen: boolean;
  onClose: () => void;
}

interface NavItem {
  label: string;
  path: string;
  icon: string;
}

/** @graph-connects none */
const NAV_ITEMS: NavItem[] = [
  {
    label: 'Dashboard',
    path: '/dashboard',
    icon: 'M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6',
  },
  {
    label: 'アウトプット',
    path: '/output',
    icon: 'M3 3v18h18M7 16l4-6 4 3 5-8',
  },
  {
    label: 'アイテム確認依頼',
    path: '/item-incident',
    icon: 'M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-6 9l2 2 4-4',
  },
  {
    label: 'VoC レポート',
    path: '/voc',
    icon: 'M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z',
  },
];

/**
 * パスの一致判定。startsWith だけだと /output が /output-foo にも当たるため、
 * 完全一致か、区切り文字 '/' が続く場合だけをアクティブとみなす。
 *
 * @graph-stack quality-atelier
 * @graph-domain Quality
 * @graph-business 現在のパスがナビゲーション項目に該当するかを判定する
 * @graph-connects none
 */
export function isActivePath(pathname: string, navPath: string): boolean {
  return pathname === navPath || pathname.startsWith(`${navPath}/`);
}

/**
 * @graph-stack quality-atelier
 * @graph-domain Quality
 * @graph-business アプリサイドバーナビゲーション。Dashboard・アウトプット・アイテム確認依頼・VoCレポートの各セクションへのリンク
 * @graph-connects none
 */
export function Sidebar({ isOpen, onClose }: SidebarProps) {
  const location = useLocation();

  return (
    <>
      {/* モバイルオーバーレイ */}
      {isOpen && (
        <div
          className="fixed inset-0 z-[var(--z-modal)] bg-black/30 md:hidden"
          onClick={onClose}
          style={{ animation: 'fadeIn 0.2s ease' }}
        />
      )}

      {/* サイドバー */}
      <aside
        className={`
          fixed inset-y-0 left-0 z-[var(--z-modal)] w-60 transform transition-transform duration-300 md:relative md:translate-x-0
          ${isOpen ? 'translate-x-0' : '-translate-x-full'}
        `}
        style={{
          background: 'var(--color-bg-sidebar)',
          backdropFilter: 'blur(var(--glass-blur))',
          WebkitBackdropFilter: 'blur(var(--glass-blur))',
          borderRight: '1px solid var(--color-border)',
        }}
      >
        {/* ロゴ */}
        <div
          className="flex h-14 items-center px-5 border-b"
          style={{ borderColor: 'var(--color-border)' }}
        >
          <div className="flex items-center gap-2">
            {/*
              ファビコンと同じ 1 ファイル（public/favicon.svg）を参照する。画像を 2 箇所に
              置くと片方だけ差し替えられてタブとサイドバーで食い違うため。
              マーク自体が赤なので、以前の「QA」バッジのような色付き背景は敷かない。
              alt は空にする。すぐ右に「Quality Atelier」のテキストがあり、読み上げが二重になる
            */}
            <img src="/favicon.svg" alt="" className="h-7 w-7" />
            <span className="text-sm font-semibold" style={{ color: 'var(--color-text-primary)' }}>
              Quality Atelier
            </span>
          </div>
        </div>

        {/* ナビゲーション */}
        <nav className="mt-4 space-y-1 px-3">
          {NAV_ITEMS.map((item) => {
            const isActive = isActivePath(location.pathname, item.path);

            return (
              <Link
                key={item.path}
                to={item.path}
                onClick={onClose}
                className={`
                  flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors
                  ${isActive ? '' : 'hover:bg-[var(--color-bg-hover)]'}
                `}
                style={{
                  background: isActive ? 'var(--color-primary-muted)' : undefined,
                  color: isActive ? 'var(--color-primary)' : 'var(--color-text-secondary)',
                }}
              >
                <svg
                  className="h-4.5 w-4.5 flex-shrink-0"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                  strokeWidth={1.5}
                >
                  <path strokeLinecap="round" strokeLinejoin="round" d={item.icon} />
                </svg>
                {item.label}
              </Link>
            );
          })}
        </nav>

        {/* フッター */}
        <div
          className="absolute bottom-0 left-0 right-0 border-t p-4"
          style={{ borderColor: 'var(--color-border)' }}
        >
          <p className="text-xs" style={{ color: 'var(--color-text-muted)' }}>
            Quality Atelier
          </p>
          <p className="text-xs" style={{ color: 'var(--color-text-disabled)' }}>
            airCloset, Inc.
          </p>
        </div>
      </aside>
    </>
  );
}
